import { useState } from 'react'
import { emit } from '../../platform/analytics/analytics'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { Money } from '../../platform/primitives/Money'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { Overlay } from '../../platform/overlay/Overlay'
import { useToast } from '../../platform/feedback/ToastProvider'
import { formatCents, parseMoney } from '../../api/parseMoney'
import { MANUAL_ACCOUNT_TYPES } from './accountGroups'
import { AccountsError } from './useAccounts'
import { useCreateManualAccount, useUpdateManualAccount } from './useManualAccount'
import { analyticsAccountType } from './accountAnalytics'
import type { Account } from './useAccounts'
import './accounts.css'

/** `AccountsError`'s own status, narrowed from `unknown` in one place — same
 *  shape `CategoryForm.tsx`/`MerchantForm.tsx` use for the identical job. */
function statusOf(cause: unknown): number | undefined {
  return cause instanceof AccountsError ? cause.status : undefined
}

/**
 * Create + edit, one component — the fields are identical and the only
 * difference is whether `editing` is present.
 *
 * **No Type selector when editing.** `AccountUpdate` accepts exactly
 * `is_hidden`/`opening_balance`/`institution_name` — sending `type` would be
 * silently ignored server-side, which looks like a successful re-type until
 * the row re-groups on its own on the next load. Offering a control the
 * server cannot honour is the bug, not a missing feature.
 *
 * **The derived balance is shown, never edited.** Trap 3: a manual account's
 * `current_balance` is `opening_balance − SUM(amount)`, computed on every
 * read and never stored — the two will not agree for any account with
 * transactions, and that is correct. Putting `current_balance` in the
 * editable field would silently rewrite history the next time this form
 * saves. The field this form edits is always `opening_balance`.
 */
export function ManualAccountForm({
  editing,
  onClose,
}: {
  editing?: Account
  onClose: () => void
}) {
  const [name, setName] = useState(editing?.name ?? '')
  const [type, setType] = useState<string>(editing?.type ?? MANUAL_ACCOUNT_TYPES[0]?.value ?? '')
  const [openingBalance, setOpeningBalance] = useState(
    editing ? formatCents(editing.openingCents ?? 0) : '',
  )
  const [institutionName, setInstitutionName] = useState(editing?.institutionName ?? '')
  // Two error slots, not one — issue #395, B3. A single `error` state fed
  // both the Name field and the balance parse failure, and Name does not
  // exist in edit mode (it is gated on `editing === undefined` below), so a
  // bad balance while editing wrote an error nothing on screen could show:
  // Save became a silent no-op. Each error now belongs to the one field that
  // can actually render it, in both modes.
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [balanceError, setBalanceError] = useState<string | undefined>(undefined)

  const create = useCreateManualAccount()
  const update = useUpdateManualAccount()
  const toast = useToast()

  const pending = create.isPending || update.isPending

  async function submit() {
    const trimmedName = name.trim()
    if (editing === undefined && trimmedName === '') {
      setNameError('Give the account a name.')
      return
    }

    let cents: number
    try {
      cents = parseMoney(openingBalance.trim() === '' ? '0' : openingBalance.trim())
    } catch {
      setBalanceError('Enter a balance like 1250.00.')
      return
    }

    try {
      if (editing === undefined) {
        const created = await create.create({
          name: trimmedName,
          type,
          openingCents: cents,
          institutionName: institutionName.trim() === '' ? undefined : institutionName.trim(),
        })
        // `manual_account_added`'s own property is `type` in the spec's
        // table; sent here as `account_type` — the key `vocabulary.ts`
        // already registers for exactly this concept, under its own M2 note
        // ("`account_type` and `account_status` are the canonical M2 enums"),
        // not by `locked_create_shown`/`account_disconnected` (architect
        // review, #399: an earlier version of this comment cited those two
        // instead — the conclusion was right, the citation was not). A bare
        // `type` cannot register at all (no STEM has an empty name), and
        // "one key per concept" means reusing the M2 key rather than adding
        // a second name for the identical thing.
        // The wire `type` is Plaid-shaped (`depository`), so it goes through
        // the same mapping `account_removed` uses: one key, one vocabulary.
        emit('manual_account_added', { account_type: analyticsAccountType({ type, subtype: null }) })
        toast.show({ kind: 'success', message: `${created.name} created` })
      } else {
        const updated = await update.update(editing.id, {
          openingCents: cents,
          institutionName: institutionName.trim() === '' ? null : institutionName.trim(),
        })
        toast.show({ kind: 'success', message: `${updated.name} saved` })
      }
      onClose()
    } catch (cause) {
      const status = statusOf(cause)
      toast.show({
        kind: 'error',
        message:
          status === 422
            ? 'Check the account details and try again.'
            : editing
              ? 'The account could not be saved.'
              : 'The account could not be created.',
      })
    }
  }

  return (
    <Overlay
      open
      onClose={onClose}
      surface="glass"
      label={editing ? `Edit ${editing.name}` : 'New account'}
    >
      <form
        className="account-form"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <h3 className="account-form__title">{editing ? 'Edit account' : 'New account'}</h3>

        {editing && editing.currentCents !== null && (
          <div className="account-form__derived">
            <span className="account-form__derived-label">Current balance (calculated)</span>
            <Money cents={editing.currentCents} currency={editing.currency} kind="stock" />
          </div>
        )}

        {editing === undefined && (
          <Field
            label="Name"
            value={name}
            error={nameError}
            size="form"
            placeholder="e.g. Cash jar"
            onChange={(e) => {
              setName(e.target.value)
              setNameError(undefined)
            }}
          />
        )}

        {editing === undefined && (
          <div className="account-form__group">
            <span className="account-form__group-label" aria-hidden="true">
              Type
            </span>
            {/* `SegmentedControl` — issue #395, B4. This used to be a fourth
                hand-rolled hidden-radio group, reproducing the primitive that
                already exists for exactly this shape. `size="form"` matches
                the density of the `Field`s around it. */}
            <SegmentedControl
              label="Type"
              name="account_type"
              options={MANUAL_ACCOUNT_TYPES}
              value={type}
              onChange={setType}
              size="form"
            />
          </div>
        )}

        <Field
          label="Opening balance"
          value={openingBalance}
          error={balanceError}
          size="form"
          placeholder="0.00"
          inputMode="decimal"
          onChange={(e) => {
            setOpeningBalance(e.target.value)
            setBalanceError(undefined)
          }}
        />

        <Field
          label="Institution (optional)"
          value={institutionName}
          size="form"
          placeholder="e.g. Self-reported"
          onChange={(e) => setInstitutionName(e.target.value)}
        />

        <div className="account-form__actions">
          <Button type="button" variant="chip" size="form" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="form" disabled={pending}>
            {editing ? (pending ? 'Saving…' : 'Save') : pending ? 'Creating…' : 'Create account'}
          </Button>
        </div>
      </form>
    </Overlay>
  )
}
